import { useMemo, useState, Fragment } from "react";
import { useFilters, useTable, usePagination, useSortBy } from "react-table";
import { useProducts } from "../productQuery";
import filterIcon from "./../icon-filter.svg";
import loadingIcon from "./../search-loading.gif";

// Input Action
const setFilteredParams = (filterArr, val, filterName) => {
  if (filterName === "main") {
    if (filterArr["main"].includes(val)) {
      filterArr["main"] = filterArr["main"].filter((n) => {
        return n !== val;
      });
    } else {
      filterArr[filterName].push(val);
    }
  } else if (
    filterName === "min" ||
    filterName === "nom" ||
    filterName === "max"
  ) {
    if (val === "") {
      filterArr[filterName] = [];
    } else {
      filterArr[filterName][0] = val;
    }
  }

  if (
    filterArr["main"].length === 0 &&
    filterArr["min"].length === 0 &&
    filterArr["nom"].length === 0 &&
    filterArr["max"].length === 0
  )
    filterArr = undefined;
  return filterArr;
};

// Custom logic
const MultipleFilter = (rows, accessor, filterValue) => {
  const arr = [];
  rows.forEach((val) => {
    filterValue["main"].forEach((filterVal) => {
      if (val.original[accessor].split(", ").includes(filterVal.toString())) {
        if (!arr.includes(val)) {
          arr.push(val);
        }
      }
    });
  });
  return arr;
};

// Custom logic for Range value
const MultipleFilterForRange = (rows, accessor, filterValue) => {
  const arr = [];
  rows.forEach((val) => {
    const newFilterValues = [];
    filterValue["main"].forEach((val) => {
      let newFilterValue = val.toString();
      if (newFilterValue.includes(" - ")) {
        newFilterValue = newFilterValue.toString().replace(" - ", ", ");
        newFilterValues.push(JSON.parse("[" + newFilterValue + "]"));
      } else {
        newFilterValue = newFilterValue.toString();
        newFilterValues.push(
          JSON.parse("[" + newFilterValue + ", " + newFilterValue + "]")
        );
      }
    });
    let accessorValue = "";
    if (val.original[accessor].includes("-")) {
      accessorValue = JSON.parse(
        "[" + val.original[accessor].toString().replace("-", ", ") + "]"
      );
    } else {
      accessorValue = JSON.parse(
        "[" + val.original[accessor] + ", " + val.original[accessor] + "]"
      );
    }

    if (newFilterValues.toString().includes(accessorValue.toString())) {
      arr.push(val);
    } else if (
      filterValue["min"][0] &&
      filterValue["max"][0] &&
      filterValue["nom"][0]
    ) {
      if (
        (filterValue["min"][0] <= accessorValue[1] &&
          filterValue["max"][0] >= accessorValue[0]) ||
        (filterValue["nom"][0] <= accessorValue[1] &&
          filterValue["nom"][0] >= accessorValue[0])
      ) {
        arr.push(val);
      }
    } else if (filterValue["min"][0] && filterValue["max"][0]) {
      if (
        filterValue["min"][0] <= accessorValue[1] &&
        filterValue["max"][0] >= accessorValue[0]
      ) {
        arr.push(val);
      }
    } else if (filterValue["min"][0]) {
      if (filterValue["min"][0] <= accessorValue[1]) {
        arr.push(val);
      }
    } else if (filterValue["max"][0]) {
      if (filterValue["max"][0] >= accessorValue[0]) {
        arr.push(val);
      }
    } else if (filterValue["nom"][0]) {
      if (
        filterValue["nom"][0] >= accessorValue[0] &&
        filterValue["nom"][0] <= accessorValue[1]
      ) {
        arr.push(val);
      }
    }
  });
  let uniqueArr = [...new Set(arr)];
  return uniqueArr;
};

// Custom logic for Set value
export const MultipleFilterForSet = (rows, accessor, filterValue) => {
  const arr = [];
  rows.forEach((val) => {
    filterValue["main"].forEach((filterVal) => {
      if (val.original[accessor].includes('"' + filterVal.toString() + '"')) {
        if (!arr.includes(val)) {
          arr.push(val);
        }
      }
    });
  });
  return arr;
};

// Filter
const SelectColumnFilter = ({
  column: {
    Header,
    filterValue = { main: [], min: [], nom: [], max: [] },
    setFilter,
    preFilteredRows,
    id,
  },
}) => {
  const options = useMemo(() => {
    const options = new Set();
    const finalOptions = new Set();
    preFilteredRows.forEach((row) => {
      if (row.values[id] !== "") {
        const rowValues = row.values[id].split(", ");
        if (rowValues.length !== 0) {
          rowValues.forEach((val) => options.add(val));
        } else {
          options.add(row.values[id]);
        }
      }
    });

    const sortedOptions = Array.from(options).sort(compareNumbers);

    const rowValues = [];
    preFilteredRows.forEach((val) => {
      rowValues.push(val.original[id].split(", "));
    });

    const rowValuesString = JSON.stringify(rowValues);

    sortedOptions.forEach((row) => {
      const count = occurrences(rowValuesString, '"' + row + '"', false);
      if (count !== 0) {
        finalOptions.add(row + " (" + count + ")");
      }
    });

    return [...finalOptions.values()];
  }, [id, preFilteredRows]);

  return (
    <Fragment>
      <div>
        <div
          style={{
            paddingLeft: "28px",
            paddingRight: "28px",
            textAlign: "left",
          }}
        >
          {options.map((option, i) => {
            const [before, after] = splitLastOccurrence(
              option.toString(),
              " ("
            );
            return (
              <Fragment key={i}>
                <div style={{ margin: "6px 0" }}>
                  <input
                    type="checkbox"
                    id={before + "-" + id}
                    name={before}
                    value={before}
                    checked={filterValue["main"].includes(before)}
                    onChange={(e) => {
                      setFilter(
                        setFilteredParams(filterValue, e.target.value, "main")
                      );
                    }}
                  ></input>
                  <label htmlFor={before + "-" + id}>{option}</label>
                </div>
              </Fragment>
            );
          })}
        </div>
      </div>
    </Fragment>
  );
};

// Filter for Range Value
const SelectColumnFilterForRange = ({
  column: {
    Header,
    filterValue = { main: [], min: [], nom: [], max: [] },
    setFilter,
    preFilteredRows,
    id,
  },
}) => {
  const options = useMemo(() => {
    const options = new Set();
    const rangeArray = [];
    preFilteredRows.forEach((row) => {
      const stringValue = row.values[id];
      rangeArray.push(stringValue.split("-"));
    });

    let uniqueRangeArray = removeDuplicates(rangeArray);

    var finalArray = uniqueRangeArray.sort(function (b, a) {
      if (a[0] == b[0]) {
        return b[1] - a[1];
      }
      return b[0] - a[0];
    });

    finalArray.forEach((row) => {
      let count = 0;
      preFilteredRows.forEach((val) => {
        const newFilterValues = val.original[id].split("-");
        let a = JSON.stringify(newFilterValues);
        let b = JSON.stringify(row);
        let c = a.indexOf(b);
        if (c != -1) {
          count++;
        }
      });

      if (Number(row[0]).toFixed(2) !== Number(row[1]).toFixed(2)) {
        options.add(
          Number(row[0]).toFixed(2) +
            " - " +
            Number(row[1]).toFixed(2) +
            " (" +
            count +
            ")"
        );
      } else {
        options.add(Number(row[0]).toFixed(2) + " (" + count + ")");
      }
    });
    return [...options.values()];
  }, [id, preFilteredRows]);
  return (
    <Fragment>
      <div className="block">
        <div
          style={{
            paddingLeft: "28px",
            paddingRight: "28px",
            textAlign: "left",
          }}
        >
          <div
            style={{
              margin: "4px 0",
            }}
          >
            <label style={{ width: "15%", float: "left" }} htmlFor="min">
              Min
            </label>
            &nbsp;&nbsp;
            <input
              type="number"
              id="min"
              value={
                filterValue["min"].length !== 0 ? filterValue["min"][0] : ""
              }
              onChange={(e) => {
                setFilter(
                  setFilteredParams(filterValue, e.target.value, "min")
                );
              }}
              style={{ width: "65%", float: "right" }}
            />
          </div>

          <div
            style={{
              margin: "8px 0",
            }}
          >
            <label style={{ width: "15%", float: "left" }} htmlFor="nom">
              Nom
            </label>
            &nbsp;&nbsp;
            <input
              type="number"
              id="nom"
              value={
                filterValue["nom"].length !== 0 ? filterValue["nom"][0] : ""
              }
              onChange={(e) => {
                setFilter(
                  setFilteredParams(filterValue, e.target.value, "nom")
                );
              }}
              style={{ width: "65%", float: "right" }}
            />
          </div>
          <div
            style={{
              margin: "8px 0 16px 0",
            }}
          >
            <label style={{ width: "15%", float: "left" }} htmlFor="max">
              Max
            </label>
            &nbsp;&nbsp;
            <input
              type="number"
              id="max"
              value={
                filterValue["max"].length !== 0 ? filterValue["max"][0] : ""
              }
              onChange={(e) => {
                setFilter(
                  setFilteredParams(filterValue, e.target.value, "max")
                );
              }}
              style={{ width: "65%", float: "right" }}
            />
          </div>
          {options.map((option, i) => {
            const [before, after] = splitLastOccurrence(
              option.toString(),
              " ("
            );
            return (
              <Fragment key={i}>
                <div style={{ margin: "6px 0" }}>
                  <input
                    type="checkbox"
                    id={before + "-" + id}
                    name={before}
                    value={before}
                    checked={filterValue["main"].includes(before)}
                    onChange={(e) => {
                      setFilter(
                        setFilteredParams(filterValue, e.target.value, "main")
                      );
                    }}
                  ></input>
                  <label htmlFor={before + "-" + id}>{option}</label>
                </div>
              </Fragment>
            );
          })}
        </div>
      </div>
    </Fragment>
  );
};

// Filter
const SelectColumnFilterForSet = ({
  column: {
    Header,
    filterValue = { main: [], min: [], nom: [], max: [] },
    setFilter,
    preFilteredRows,
    id,
  },
}) => {
  const options = useMemo(() => {
    const setArray = new Set();
    const finalOptions = new Set();
    preFilteredRows.forEach((row) => {
      const stringValue = row.values[id];
      if (isJSONArray("[" + stringValue + "]")) {
        var arrayValue = JSON.parse("[" + stringValue + "]");
        if (arrayValue[0]) {
          arrayValue[0].forEach((val) => {
            setArray.add(val);
          });
        }
      }
    });

    const sortedOptions = Array.from(setArray).sort();
    const rowValues = [];
    preFilteredRows.forEach((val) => {
      rowValues.push(val.original[id]);
    });

    sortedOptions.forEach((row) => {
      let count = 0;
      rowValues.forEach((val) => {
        if (val.includes('"' + row + '"')) {
          count++;
        }
      });
      finalOptions.add(row + " (" + count + ")");
    });

    return [...finalOptions.values()];
  }, [id, preFilteredRows]);

  return (
    <Fragment>
      <div className="block">
        <div
          style={{
            paddingLeft: "28px",
            paddingRight: "28px",
            textAlign: "left",
          }}
        >
          {options.map((option, i) => {
            const [before, after] = splitLastOccurrence(
              option.toString(),
              " ("
            );
            return (
              <Fragment key={i}>
                <div style={{ margin: "6px 0" }}>
                  <input
                    type="checkbox"
                    id={before + "-" + id}
                    name={before}
                    value={before}
                    checked={filterValue["main"].includes(before)}
                    onChange={(e) => {
                      setFilter(
                        setFilteredParams(filterValue, e.target.value, "main")
                      );
                    }}
                  ></input>
                  <label htmlFor={before + "-" + id}>{option}</label>
                </div>
              </Fragment>
            );
          })}
        </div>
      </div>
    </Fragment>
  );
};

function occurrences(string, subString, allowOverlapping) {
  string += "";
  subString += "";
  if (subString.length <= 0) return string.length + 1;

  var n = 0,
    pos = 0,
    step = allowOverlapping ? 1 : subString.length;

  while (true) {
    pos = string.indexOf(subString, pos);
    if (pos >= 0) {
      ++n;
      pos += step;
    } else break;
  }
  return n;
}

function compareNumbers(a, b) {
  const newA = !isNaN(Number(a)) ? Number(a) : a;
  const newB = !isNaN(Number(b)) ? Number(b) : b;
  if (typeof newA === "number" && typeof newB === "number") {
    return newA - newB;
  }
  // check for num vs string
  if (typeof newA === "number" && typeof newB === "string") {
    return -1;
  }
  // check for string vs num
  if (typeof newA === "string" && typeof newB === "number") {
    return 1;
  }
  // check for string vs string
  if (typeof newA === "string" && typeof newB === "string") {
    if (newA < newB) return -1;
    else return 1;
  }

  return 0;
}

function splitLastOccurrence(str, substring) {
  const lastIndex = str.lastIndexOf(substring);
  const before = str.slice(0, lastIndex);
  const after = str.slice(lastIndex + 1);

  return [before, after];
}

function removeDuplicates(a) {
  var seen = {};
  var out = [];
  var len = a.length;
  var j = 0;
  for (var i = 0; i < len; i++) {
    var item = a[i];
    if (seen[item] !== 1) {
      seen[item] = 1;
      out[j++] = item;
    }
  }
  return out;
}

// Check if given string is JSON Array
function isJSONArray(str) {
  try {
    const parsedJSON = JSON.parse(str);
    return Array.isArray(parsedJSON);
  } catch (error) {
    return false;
  }
}

function replaceNonHtmlQuotes(obj) {
  if (typeof obj === "string") {
    // Check if the string is within square brackets (potential array)
    if (obj.startsWith('[') && obj.endsWith(']')) {
      try {
        // Attempt to parse as JSON array (ignoring potential errors)
        const parsedArray = JSON.parse(obj);
        // Recursively replace quotes within the parsed array elements
        return JSON.stringify(parsedArray.map(replaceNonHtmlQuotes));
      } catch (error) {
        // Handle potential parsing errors (string might not be a valid JSON array)
        return obj; // Leave the string as-is
      }
    } else {
      return obj.replace(/"(?![^<]*>)/g, "'"); // Replace within strings except inside HTML tags
    }
  } else if (typeof obj === "object") {
    for (const key in obj) {
      obj[key] = replaceNonHtmlQuotes(obj[key]); // Recursively apply to nested objects
    }
  }
  return obj;
}

function Table({ columns, data }) {
  const [filterShow, setFilterShow] = useState(false);
  columns.sort((a, b) => a.filterSort - b.filterSort);

  // Use the state and functions returned from useTable to build your UI
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setFilter,
    setAllFilters,
    allColumns,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      useFilters,
      initialState: {
        pageSize: 10,
        pageIndex: 0,
        hiddenColumns: columns.map((column) => {
          if (column.show === false) return column.accessor || column.id;
        }),
      },
    },
    useFilters,
    useSortBy,
    usePagination
  );

  allColumns.sort((a, b) => a.filterOrder - b.filterOrder);

  let i = 0;

  // Render the table UI
  return (
    <>
      <div
        style={{
          border: "#E7E7E8 solid 1px",
          display: "flex",
          padding: "20px 16px",
          gap: "8px",
          alignItems: "center",
        }}
      >
        <img src={filterIcon} />
        <span style={{ flex: "1" }}>筛选</span>
        <span
          style={{
            color: "#999",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: "400",
            lineHeight: "24px",
            cursor: "pointer",
          }}
          onClick={() => {
            setAllFilters([]);
          }}
        >
          重置
        </span>
        <span
          style={{
            color: "#202F5C",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: "400",
            lineHeight: "24px",
            paddingLeft: "12px",
            cursor: "pointer",
          }}
          onClick={() => setFilterShow(!filterShow)}
          id="expand-product-filter"
        >
          查看
        </span>
      </div>
      <div
        style={{
          display: filterShow == true ? "flex" : "none",
          flexWrap: "nowrap",
          flexDirection: "row",
          overflow: "auto",
          overflowX: "scroll",
          overflowY: "clip",
          borderStyle: "solid",
          borderWidth: "0 1px 1px 1px",
          borderColor: "#E7E7E8",
          paddingInlineStart: "0px",
          maxHeight: "480px",
        }}
      >
        {allColumns.map((column, j) => {
          let valueArray = [];
          column.preFilteredRows.forEach((row) => {
            if (row.values[column.key] !== "") {
              valueArray.push(row.values[column.key]);
            }
          });

          i++;
          if (j !== 0 && j !== 1) {
            return column.canFilter ? (
              valueArray.length !== 0 ? (
                <div
                  style={{
                    pointerEvents: "auto",
                    opacity: 1,
                    display: "inline-block",
                    maxHeight: "480px",
                    backgroundColor: i % 2 === 0 ? "#F9F9F9" : "#FFFFFF",
                    borderRight: "1px solid #E7E7E8",
                    borderBottom: "1px solid #E7E7E8",
                  }}
                >
                  <div style={{ padding: "28px", textAlign: "left" }}>
                    {column.Header}
                  </div>
                  <div
                    style={{
                      overflowY: "scroll",
                      width: "240px",
                      height: "400px",
                    }}
                    key={j}
                  >
                    {column.render("Filter")}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    pointerEvents: "none",
                    opacity: 0.4,
                    display: "inline-block",
                    maxHeight: "480px",
                    backgroundColor: i % 2 === 0 ? "#F9F9F9" : "#FFFFFF",
                    borderRight: "1px solid #E7E7E8",
                    borderBottom: "1px solid #E7E7E8",
                  }}
                >
                  <div style={{ padding: "28px", textAlign: "left" }}>
                    {column.Header}
                  </div>
                  <div
                    style={{
                      overflowY: "scroll",
                      width: "240px",
                      height: "400px",
                    }}
                    key={j}
                  >
                    <div style={{ padding: "0 36px" }}>无选择</div>
                  </div>
                </div>
              )
            ) : null;
          }
        })}
      </div>
      <div style={{ margin: "24px 4px", float: "right" }}>
        <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
          {"<<"}
        </button>{" "}
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>
          {"<"}
        </button>{" "}
        <button onClick={() => nextPage()} disabled={!canNextPage}>
          {">"}
        </button>{" "}
        <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
          {">>"}
        </button>{" "}
        <span>
          第{" "}
          <strong>
            {pageIndex + 1} / {pageOptions.length}
          </strong>{" "}
          页
        </span>
        <span>
          {"  "}|{"  "}跳到:{" "}
          <input
            type="number"
            defaultValue={pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              gotoPage(page);
            }}
            className="pagination-input"
          />
        </span>{" "}
        {/* <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
          }}
        >
          {[10, 20, 30, 40, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize}
            </option>
          ))}
        </select> */}
      </div>
      <table style={{ marginTop: 30, width: "100%" }} {...getTableProps()}>
        <thead
          style={{
            backgroundColor: "#E7E7E8",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: "500",
          }}
        >
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, i) => (
                <th
                  style={{ border: "1px solid #E7E7E8", padding: "24px" }}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className={`${
                    column.isSorted
                      ? column.isSortedDesc
                        ? "desc"
                        : "asc"
                      : ""
                  } ${i > 1 ? " attribute-column" : ""}`}
                >
                  {column.render("Header")}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell, i) => {
                  return (
                    <td
                      {...cell.getCellProps()}
                      className={`${
                        i > 1 ? " attribute-column attribute-column-value" : ""
                      }`}
                      style={{ padding: "0 4px" }}
                    >
                      {cell.render("Cell")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ margin: "24px 4px", float: "right" }}>
        <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
          {"<<"}
        </button>{" "}
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>
          {"<"}
        </button>{" "}
        <button onClick={() => nextPage()} disabled={!canNextPage}>
          {">"}
        </button>{" "}
        <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
          {">>"}
        </button>{" "}
        <span>
          第{" "}
          <strong>
            {pageIndex + 1} / {pageOptions.length}
          </strong>{" "}
          页
        </span>
        <span>
          {"  "}|{"  "}跳到:{" "}
          <input
            type="number"
            defaultValue={pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              gotoPage(page);
            }}
            className="pagination-input"
          />
        </span>{" "}
        {/* <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
          }}
        >
          {[10, 20, 30, 40, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize}
            </option>
          ))}
        </select> */}
      </div>
    </>
  );
}

// Use a custom filter function
const customFilterFunction = (rows, id, filterValue) =>
  rows.filter((row) => row.original.age >= filterValue);

// Main
const ProductsTableInstance = ({ id }) => {
  const { isLoading, error, data: productData, isFetching } = useProducts(id);
  const [data, setData] = useState([]);
  const [columnData, setColumnData] = useState([]);

  useMemo(() => {
    if (productData) {
      const processedProductDataObject = replaceNonHtmlQuotes(JSON.parse(JSON.stringify(productData.data)));
      setData(processedProductDataObject);

      const columns = productData.columns;
      const tempColumns = columns.map((value) => {
        const type = value["data"].split("_")[1];
        if (type === "range") {
          const column = {
            Header: value["title"],
            accessor: value["data"],
            key: value["data"],
            Cell: (props) => {
              const range = props.value.split("-");
              if (range[0] !== range[1]) {
                return `${Number(range[0])
                  .toFixed(2)
                  .toString()
                  .replace(/.00/g, ".0")}-${Number(range[1])
                  .toFixed(2)
                  .toString()
                  .replace(/.00/g, ".0")}`;
              } else {
                return Number(range[0])
                  .toFixed(2)
                  .toString()
                  .replace(/.00/g, ".0");
              }
            },
            show: value["visible"],
            disableFilters: !value["filterable"],
            Filter: SelectColumnFilterForRange,
            filter: MultipleFilterForRange,
            order: value["sort"],
            filterOrder: value["filterSort"],
          };
          return column;
        } else if (type === "set") {
          const column = {
            Header: value["title"],
            accessor: value["data"],
            key: value["data"],
            Cell: (props) => {
              return value["data"] === "image" || value["data"] === "title" ? (
                <span dangerouslySetInnerHTML={{ __html: props.value }} />
              ) : (
                props.value
              );
            },
            show: value["visible"],
            disableFilters: !value["filterable"],
            Filter: SelectColumnFilterForSet,
            filter: MultipleFilterForSet,
            order: value["sort"],
            filterOrder: value["filterSort"],
          };
          return column;
        } else {
          const column = {
            Header: value["title"],
            accessor: value["data"],
            key: value["data"],
            Cell: (props) => {
              return value["data"] === "image" ||
                value["data"] === "title" ||
                value["data"] === "actions" ? (
                <span dangerouslySetInnerHTML={{ __html: props.value }} />
              ) : (
                props.value
              );
            },
            show: value["visible"],
            disableFilters: !value["filterable"],
            Filter: SelectColumnFilter,
            filter: MultipleFilter,
            order: value["sort"],
            filterOrder: value["filterSort"],
          };
          return column;
        }
      });
      setColumnData(tempColumns);
    }
  }, [productData]);

  if (isLoading) {
    return (
      <>
        <center>
          <img
            src={loadingIcon}
            style={{ height: "24px", marginTop: "240px" }}
          />{" "}
          <div
            style={{
              textAlign: "center",
              margin: "6px 0",
              fontWeight: "bold",
              fontSize: "20px",
            }}
          >
            加载中...
          </div>
        </center>
      </>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <Table columns={columnData} data={data} />
    </div>
  );
};

export default ProductsTableInstance;
